import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'antd-mobile';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

// 扩展MediaTrackCapabilities类型以支持手电筒
interface ExtendedMediaTrackCapabilities extends MediaTrackCapabilities {
    torch?: boolean;
}

// 扩展MediaTrackConstraintSet类型以支持手电筒
interface ExtendedMediaTrackConstraintSet extends MediaTrackConstraintSet {
    torch?: boolean;
}

interface MockPDAScannerProps {
    onScanSuccess: (code: string) => void;
    onScanError?: (error: string) => void;
    fps?: number;
    qrboxWidth?: number;
    qrboxHeight?: number;
    formatsToSupport?: Html5QrcodeSupportedFormats[];
}

const MockPDAScanner: React.FC<MockPDAScannerProps> = ({
    onScanSuccess,
    onScanError,
    fps = 10,
    qrboxWidth = 300,
    qrboxHeight = 120,
    formatsToSupport = [
        // Html5QrcodeSupportedFormats.EAN_13,
        // Html5QrcodeSupportedFormats.EAN_8,
        // Html5QrcodeSupportedFormats.CODE_39,
        // Html5QrcodeSupportedFormats.CODE_93,
        Html5QrcodeSupportedFormats.CODE_128,
        // Html5QrcodeSupportedFormats.UPC_A,
        // Html5QrcodeSupportedFormats.UPC_E,
        Html5QrcodeSupportedFormats.QR_CODE,
    ],
}) => {
    const [scanning, setScanning] = useState<boolean>(true);
    const scannerRef = useRef<Html5Qrcode | null>(null);
    const scannerContainerRef = useRef<HTMLDivElement>(null);
    const videoTrackRef = useRef<MediaStreamTrack | null>(null);

    useEffect(() => {
        if (scanning && scannerContainerRef.current) {
            const id = 'barcode-scanner';
            const scannerContainer = scannerContainerRef.current;

            // 确保容器是空的
            scannerContainer.innerHTML = `<div id="${id}"></div>`;

            const scanner = new Html5Qrcode(id);
            scannerRef.current = scanner;

            const config = {
                fps,
                // qrbox: { width: qrboxWidth, height: qrboxHeight },
                qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
                    const width = Math.max(viewfinderWidth * 0.9, qrboxWidth);
                    const height = Math.max(viewfinderHeight * 0.8, qrboxHeight);
                    return { width, height };
                },
                formatsToSupport
            };

            scanner.start(
                { facingMode: 'environment' },
                config,
                (decodedText) => {
                    onScanSuccess(decodedText);
                },
                (errorMessage) => {
                    // 实时扫描过程中的常规错误，可以忽略
                    if (onScanError) {
                        onScanError(errorMessage);
                    }
                }
            ).catch(err => {
                Toast.show({
                    icon: 'fail',
                    content: 'Unable to start camera',
                });
            });

            // 获取视频流
            const video = document.getElementById(id)?.getElementsByTagName('video')[0];
            if (video && video.srcObject instanceof MediaStream) {
                const videoTracks = video.srcObject.getVideoTracks();
                if (videoTracks.length > 0) {
                    videoTrackRef.current = videoTracks[0];
                }
            }
        }

        return () => {
            stopScanner();
        };
    }, [scanning, fps, qrboxWidth, qrboxHeight, formatsToSupport, onScanSuccess, onScanError]);

    const stopScanner = () => {
        if (scannerRef.current && scannerRef.current.isScanning) {
            scannerRef.current.stop().catch(err => {
            });
        }
    };

    const resetScanner = () => {
        setScanning(true);
    };

    const pauseScanner = () => {
        setScanning(false);
    };

    return (
        <div ref={scannerContainerRef} className="w-full h-full"></div>
    );
};

export const MockPDA = ({
    onScanSuccess,
}: {
    onScanSuccess: (code: string) => void;
}) => {
    return (
        <div className="w-full h-full">
            <MockPDAScanner onScanSuccess={onScanSuccess} />
        </div>
    );
};
